/**
 * Bounded + retried fetch of the checkout session config (refs #5556).
 *
 * Kept in its own module (not the package entry, `index.ts`) so the retry mechanism and its
 * helpers are unit-testable via a direct internal import without widening the package's public
 * API. Only `SessionConfigTimeoutError` is re-exported publicly from `index.ts`.
 */

// Per-attempt bound on the session-config GET. Normal config is ~0.3s; the field hangs that caused
// this (a stalled-but-open response that never settled) ran 60–336s. 8s is a ~15–30× margin over
// normal and far under any real hang, so a healthy request is never cut off and a hung one is
// abandoned fast. Reviewer decision (mirrors the #5536 family: a named *_TIMEOUT_MS const with a
// decision comment). PER ATTEMPT, not total.
export const SESSION_CONFIG_FETCH_TIMEOUT_MS = 8_000;
// 1 try + 1 retry. The dominant failure is HTTP/2 connection poisoning, and browser `fetch` after
// abort() reuses the poisoned pooled connection (there is no "new connection" knob), so a 2nd or
// 3rd attempt just re-hangs for no yield; a single retry still catches a transient 5xx/429
// recovery. And since there is no direct-mode fallback (exhaustion = a hard checkout failure),
// failing loud sooner gets the buyer to the real cure — a page refresh — faster. The small cap also
// avoids amplifying a correlated saturation incident (see PR #5543).
export const SESSION_CONFIG_MAX_ATTEMPTS = 2;
// Full-jitter exponential backoff: delay = random()·min(cap, base·2^n). Full jitter (not a fixed
// exponential schedule) de-synchronizes the retrying herd during a shared incident.
const SESSION_CONFIG_BACKOFF_BASE_MS = 500;
const SESSION_CONFIG_BACKOFF_CAP_MS = 4_000;

/**
 * Stable, documented marker that every {@link SessionConfigTimeoutError} message begins with, and
 * also the `console.error` / analytics event name. Merchants match on THIS string, not on
 * `instanceof` (see the class note).
 */
export const SESSION_CONFIG_FETCH_FAILED = "session_config_fetch_failed";

/**
 * Terminal failure of the session-config fetch. Thrown by {@link fetchSessionConfigWithRetry} when
 * every attempt failed (timeout, network error, 5xx or 429 exhausted) or on a non-retryable
 * response (a 4xx, or a 2xx with no usable `card_tokenization_mode`). `index.ts` stores it as
 * `sessionConfigError` STATE so `_sessionConfigReady` never rejects. There is no direct-mode
 * fallback: a VGS session rejects a raw PAN (422), so falling back to direct was a dead end anyway.
 *
 * Merchant matching: the submit wrapper flattens a thrown error to `onError({ root: error.message })`,
 * so the typed instance does NOT survive to `onError` — do NOT match on `instanceof`. Instead the
 * `.message` always BEGINS with the stable {@link SESSION_CONFIG_FETCH_FAILED} marker (prefixed by
 * this constructor), so a merchant can detect this failure with
 * `error.root?.startsWith("session_config_fetch_failed")`.
 */
export class SessionConfigTimeoutError extends Error {
  readonly attempts: number;
  constructor(detail: string, attempts: number) {
    super(`${SESSION_CONFIG_FETCH_FAILED}: ${detail}`);
    this.name = "SessionConfigTimeoutError";
    this.attempts = attempts;
  }
}

// Seam for the retry backoff. Uses global `setTimeout` so a vitest test can drive it with fake
// timers, and `Math.random` so a test can make the jitter deterministic by mocking it.
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Full-jitter backoff for the retry after a (0-based) failed attempt index. */
const sessionConfigBackoffMs = (failedAttemptIndex: number): number =>
  Math.random() * Math.min(SESSION_CONFIG_BACKOFF_CAP_MS, SESSION_CONFIG_BACKOFF_BASE_MS * 2 ** failedAttemptIndex);

/**
 * Parse a `Retry-After` header (delta-seconds or an HTTP date) into a non-negative delay in ms, or
 * `undefined` when the header is absent or unparseable. Does NOT clamp to the backoff cap — the
 * retry loop applies the cap.
 */
export const parseRetryAfterMs = (headerValue: string | null): number | undefined => {
  if (!headerValue) return undefined;
  const seconds = Number(headerValue);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const dateMs = Date.parse(headerValue);
  return Number.isNaN(dateMs) ? undefined : Math.max(0, dateMs - Date.now());
};

/**
 * Fetch the session config, bounding each attempt and retrying a small, capped number of times.
 * Returns the parsed 2xx body on success (the caller honors whatever mode it carries, including a
 * legitimate `direct`). Throws {@link SessionConfigTimeoutError} when the fetch fails terminally.
 * Retries ONLY retryable failures — our abort/timeout, a network error, a 5xx, or a 429 — and never
 * a 4xx (a bad/expired token won't be cured by retrying). Honors `Retry-After` on 429/503.
 *
 * With 2 attempts there is only ONE backoff (capped at 4s), so the worst case is 2×8s fetch + one
 * ≤4s backoff ≈ 20s — no separate total-wall-clock clamp is needed.
 */
// biome-ignore lint/suspicious/noExplicitAny: session API payload
export const fetchSessionConfigWithRetry = async (url: string): Promise<any> => {
  let lastDetail = "unknown";
  for (let attempt = 1; attempt <= SESSION_CONFIG_MAX_ATTEMPTS; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SESSION_CONFIG_FETCH_TIMEOUT_MS);
    let retryAfterMs: number | undefined;
    try {
      const resp = await fetch(url, { signal: controller.signal });
      if (resp.ok) {
        return await resp.json();
      }
      // A 4xx (except 429) is terminal — retrying a bad/expired token just adds load.
      if (resp.status !== 429 && resp.status < 500) {
        throw new SessionConfigTimeoutError(`session config fetch returned HTTP ${resp.status}`, attempt);
      }
      // 5xx or 429 — retryable. Honor an explicit Retry-After when the server sent one.
      lastDetail = `HTTP ${resp.status}`;
      retryAfterMs = parseRetryAfterMs(resp.headers.get("Retry-After"));
    } catch (err) {
      // A terminal 4xx thrown just above must not be retried.
      if (err instanceof SessionConfigTimeoutError) throw err;
      // Our AbortError (the per-attempt timeout) or a network/parse error — retryable.
      lastDetail = err instanceof Error ? err.name || err.message : String(err);
    } finally {
      clearTimeout(timer);
    }
    if (attempt >= SESSION_CONFIG_MAX_ATTEMPTS) {
      throw new SessionConfigTimeoutError(
        `session config fetch failed after ${attempt} attempts (${lastDetail})`,
        attempt,
      );
    }
    // The one backoff: jitter or an honored Retry-After, clamped to the per-sleep cap.
    const backoff = Math.min(retryAfterMs ?? sessionConfigBackoffMs(attempt - 1), SESSION_CONFIG_BACKOFF_CAP_MS);
    await sleep(backoff);
  }
  // Unreachable: the loop always returns or throws. Satisfies the type checker.
  throw new SessionConfigTimeoutError("session config fetch failed", SESSION_CONFIG_MAX_ATTEMPTS);
};
